import { Button, Flex, Modal } from "antd";
import { CSSProperties, useMemo, useState } from "react";
import { lightenDarkenColor, useCSVReader } from "react-papaparse";
import { IImportExpensePayload } from "../../../types/types";

export default function ImportExpensesModal({
  open,
  onCancel,
  onSuccess,
}: {
  onSuccess: (data: IImportExpensePayload[]) => void;
  open: boolean;
  onCancel: () => void;
}) {
  const { CSVReader } = useCSVReader();
  const [csvUploadResp, setCsvUploadResp] = useState<ICSVUploadSuccessResp>();
  const DEFAULT_REMOVE_HOVER_COLOR = "#A01919";
  const REMOVE_HOVER_COLOR_LIGHT = lightenDarkenColor(
    DEFAULT_REMOVE_HOVER_COLOR,
    40,
  );
  const [zoneHover, setZoneHover] = useState(false);
  const [removeHoverColor, setRemoveHoverColor] = useState(
    DEFAULT_REMOVE_HOVER_COLOR,
  );

  const foundColumns: string[] = useMemo(() => {
    if (csvUploadResp) {
      return csvUploadResp.data[0];
    }
    return [];
  }, [csvUploadResp]);

  const columnIndices: IColumnIndices | undefined = useMemo(() => {
    if (foundColumns?.length > 0) {
      return {
        expenseDate:
          foundColumns.findIndex((f) => f.toLowerCase().includes("date")) ?? -1,
        amount:
          foundColumns.findIndex((f) => f.toLowerCase().includes("amount")) ??
          -1,
        comments:
          foundColumns.findIndex((f) =>
            f.toLowerCase().includes("description"),
          ) ?? -1,
      };
    }
    return undefined;
  }, [foundColumns]);

  // same as above but will use the column name to show in UI
  const columnNamesMap: IColumnNameMap | undefined = useMemo(() => {
    if (foundColumns?.length > 0) {
      return {
        expenseDate:
          foundColumns.find((f) => f.toLowerCase().includes("date")) ?? "",
        amount:
          foundColumns.find((f) => f.toLowerCase().includes("amount")) ?? "",
        comments:
          foundColumns.find((f) => f.toLowerCase().includes("description")) ??
          "",
      };
    }
    return undefined;
  }, [foundColumns]);

  const payload: IImportExpensePayload[] = useMemo(() => {
    if (csvUploadResp && columnIndices) {
      const results: IImportExpensePayload[] = [];
      // start from index number 1. index 0 is columns
      for (let i = 1; i < csvUploadResp.data.length; i++) {
        const row = csvUploadResp.data[i];
        results.push({
          amount: row[columnIndices.amount] ? +row[columnIndices.amount] : 0,
          category: "",
          comments: row[columnIndices.comments],
          expenseDate: row[columnIndices.expenseDate],
        });
      }
      return results;
    }
    return [];
  }, [csvUploadResp, columnIndices]);

  console.log("payload is", payload, foundColumns, columnIndices);

  return (
    <Modal
      title="Import From CSV"
      open={open}
      onCancel={onCancel}
      footer={null}
    >
      <Flex gap={30} vertical style={{ padding: 10 }}>
        <div className="flex justify-end">
          <a
            href="https://app.expensehut.com/help/import-expenses"
            target="_blank"
            rel="noreferrer"
            className="text-blue-500 underline"
          >
            Help
          </a>
        </div>
        <CSVReader
          onUploadAccepted={(results: ICSVUploadSuccessResp) => {
            console.log("results are ", results.data);
            setZoneHover(false);
            setCsvUploadResp(results);
          }}
          onDragOver={(event: DragEvent) => {
            event.preventDefault();
            setZoneHover(true);
          }}
          onDragLeave={(event: DragEvent) => {
            event.preventDefault();
            setZoneHover(false);
          }}
        >
          {({
            getRootProps,
            acceptedFile,
            getRemoveFileProps,
            Remove,
          }: any) => (
            <>
              <div
                {...getRootProps()}
                style={Object.assign(
                  {},
                  styles.zone,
                  zoneHover && styles.zoneHover,
                )}
              >
                {acceptedFile ? (
                  <>
                    <div style={styles.file}>
                      <div className="divCenterAlign">
                        <span>{acceptedFile.name}</span>
                      </div>

                      <div
                        {...getRemoveFileProps()}
                        style={styles.remove}
                        onMouseOver={(event: Event) => {
                          event.preventDefault();
                          setRemoveHoverColor(REMOVE_HOVER_COLOR_LIGHT);
                        }}
                        onMouseOut={(event: Event) => {
                          event.preventDefault();
                          setRemoveHoverColor(DEFAULT_REMOVE_HOVER_COLOR);
                        }}
                      >
                        <Remove color={removeHoverColor} />
                      </div>
                    </div>
                  </>
                ) : (
                  "Drop CSV file here or click to upload"
                )}
              </div>
            </>
          )}
        </CSVReader>

        {columnNamesMap && (
          <div>
            <p className="my-3 text-xs">
              The following columns from the sheet will be used to add into our
              tables:{" "}
            </p>
            <div className="flex flex-col">
              <div className="grid grid-cols-2">
                <p>Date: </p>
                <strong
                  className={`${columnNamesMap.expenseDate ? "" : "text-red-500"} `}
                >
                  {columnNamesMap.expenseDate ?? "--Missing--"}
                </strong>
              </div>
              <div className="grid grid-cols-2">
                <p>Amount: </p>
                <strong
                  className={`${columnNamesMap.amount ? "" : "text-red-500"} `}
                >
                  {columnNamesMap.amount ?? "--Missing--"}
                </strong>{" "}
              </div>
              <div className="grid grid-cols-2">
                <p>Comments: </p>
                <strong
                  className={`${columnNamesMap.comments ? "" : "text-red-500"} `}
                >
                  {columnNamesMap.comments ?? "--Missing--"}
                </strong>{" "}
              </div>
            </div>

            <p className="my-3 text-xs text-gray-600">
              {" "}
              If there is discrepancy, please correct the column names in your
              CSV. It should only have{" "}
              <strong>Date, Description and Amount</strong> column.
            </p>

            <div className="flex justify-center my-10">
              <Flex gap={10}>
                <Button
                  type="primary"
                  disabled={foundColumns.length === 0}
                  onClick={() => onSuccess(payload)}
                >
                  Proceed
                </Button>
              </Flex>
            </div>
          </div>
        )}
      </Flex>
    </Modal>
  );
}

const styles = {
  zone: {
    alignItems: "center",
    border: `2px dashed grey`,
    borderRadius: 20,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    justifyContent: "center",
    padding: 20,
  } as CSSProperties,
  file: {
    background: "linear-gradient(to bottom, #EEE, #DDD)",
    borderRadius: 20,
    display: "flex",
    height: 60,
    width: "90%",
    position: "relative",
    zIndex: 10,
    flexDirection: "column",
    justifyContent: "center",
    padding: 10,
  } as CSSProperties,
  info: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    paddingLeft: 10,
    paddingRight: 10,
  } as CSSProperties,
  size: {
    backgroundColor: "lightgrey",
    borderRadius: 3,
    marginBottom: "0.5em",
    justifyContent: "center",
    display: "flex",
  } as CSSProperties,
  name: {
    backgroundColor: "lightgrey",
    borderRadius: 3,
    fontSize: 12,
    marginBottom: "0.5em",
  } as CSSProperties,
  progressBar: {
    bottom: 14,
    position: "absolute",
    width: "100%",
    paddingLeft: 10,
    paddingRight: 10,
  } as CSSProperties,
  zoneHover: {
    borderColor: "lightgrey",
  } as CSSProperties,
  default: {
    borderColor: "grey",
  } as CSSProperties,
  remove: {
    height: 23,
    position: "absolute",
    right: 6,
    top: 6,
    width: 23,
  } as CSSProperties,
};

interface ICSVUploadSuccessResp {
  data: any[]; // this is actual data
  errors: any[];
  meta: any[]; // no idea but keep it
}
interface IColumnIndices {
  expenseDate: number;
  amount: number;
  comments: number;
}

interface IColumnNameMap {
  expenseDate: string;
  amount: string;
  comments: string;
}
